import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Modal,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {Link as RouterLink} from "react-router-dom";

import {useAlert} from "../context/AlertContext";
import AppTableHeaderCell from "./AppTableHeaderCell";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import AppModalBox from "./AppModalBox";

import {deleteStream, getStreamById, getStreams, updateStream} from "../api/api";
import {Stream} from "../types";

interface Props {
    onAdd: () => void;
    refreshTrigger: boolean;
    refreshStreams: () => void;
}

const getInitials = (name?: string) => {
    if (!name || !name.trim()) return "?";

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const StreamsList = ({onAdd, refreshTrigger, refreshStreams}: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();

    const [streams, setStreams] = useState<Stream[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 10;

    const [searchName, setSearchName] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [loading, setLoading] = useState(false);

    const [streamToDelete, setStreamToDelete] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [editingStream, setEditingStream] = useState<Stream | null>(null);
    const [editModalLoading, setEditModalLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    const [editName, setEditName] = useState("");
    const [editStartingDate, setEditStartingDate] = useState("");
    const [editEndingDate, setEditEndingDate] = useState("");

    const fetchStreamsPage = async () => {
        try {
            setLoading(true);
            const {streams, totalPages} = await getStreams(page - 1, pageSize, searchName);
            setStreams(streams);
            setTotalPages(totalPages);
        } catch (error: any) {
            showAlert(error?.message ?? t("streamslist.errors.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStreamsPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, refreshTrigger, searchName]);

    useEffect(() => {
        setPage(1);
    }, [searchName]);

    const openEdit = async (stream: Stream) => {
        try {
            setEditingStream(stream);
            setEditModalLoading(true);

            const details: Stream = await getStreamById(stream.id);

            setEditName(details.name ?? "");
            setEditStartingDate(details.startingDate ?? "");
            setEditEndingDate(details.endingDate ?? "");
        } catch (e: any) {
            showAlert(e?.message || t("streamslist.errors.loadDetailsFailed"), "error");
            setEditingStream(null);
        } finally {
            setEditModalLoading(false);
        }
    };

    const closeEditModal = () => {
        setEditingStream(null);
        setEditModalLoading(false);
        setEditSaving(false);
        setEditName("");
        setEditStartingDate("");
        setEditEndingDate("");
    };

    const handleEditStream = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingStream) return;

        if (!editName.trim()) {
            showAlert(t("streamslist.errors.nameRequired"), "error");
            return;
        }

        if (!editStartingDate) {
            showAlert(t("streamslist.errors.startingDateRequired"), "error");
            return;
        }

        try {
            setEditSaving(true);

            await updateStream({
                id: editingStream.id,
                name: editName.trim(),
                startingDate: editStartingDate,
                endingDate: editEndingDate ? editEndingDate : null,
            });

            showAlert(t("streamslist.alerts.updated"), "success");
            closeEditModal();
            refreshStreams?.();
        } catch (error: any) {
            showAlert(error?.message || t("streamslist.errors.updateFailed"), "error");
        } finally {
            setEditSaving(false);
        }
    };

    const handleDeleteStream = async () => {
        if (!streamToDelete) return;

        try {
            setDeleteLoading(true);
            await deleteStream(streamToDelete);

            showAlert(t("streamslist.alerts.deleted"), "success");
            setStreamToDelete(null);
            refreshStreams?.();
        } catch (error: any) {
            showAlert(error?.message || t("streamslist.errors.deleteFailed"), "error");
        } finally {
            setDeleteLoading(false);
        }
    };

    const onSearch = () => {
        setSearchName(searchInput.trim());
    };

    return (
        <>
            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: {xs: "stretch", sm: "center"},
                            gap: 1.5,
                            px: {xs: 1.5, md: 3},
                            py: 2,
                            flexDirection: {xs: "column", sm: "row"},
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: {xs: "100%", sm: "auto"},
                            }}
                        >
                            <TextField
                                type="search"
                                size="small"
                                placeholder={t("streamslist.searchByName")}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        onSearch();
                                    }
                                }}
                                sx={{
                                    width: {xs: "100%", sm: 320},
                                    flexGrow: {xs: 1, sm: 0},
                                }}
                            />

                            <IconButton
                                onClick={onSearch}
                                disabled={loading}
                                aria-label={t("common.search")}
                                sx={(theme) => ({
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: theme.palette.action.hover,
                                    color: theme.palette.text.primary,
                                    flexShrink: 0,
                                    "&:hover": {
                                        backgroundColor: theme.palette.action.selected,
                                    },
                                })}
                            >
                                <SearchIcon fontSize="small"/>
                            </IconButton>
                        </Box>

                        <Box sx={{flexGrow: 1, display: {xs: "none", sm: "block"}}}/>

                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                width: {xs: "100%", sm: "auto"},
                                px: {xs: 1.25, sm: 0},
                                py: {xs: 1, sm: 0},
                                borderRadius: {xs: 2.5, sm: 0},
                                backgroundColor: {
                                    xs: theme.palette.action.hover,
                                    sm: "transparent",
                                },
                            })}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{fontWeight: 700}}>
                                {t("common.pageOf", {page, totalPages})}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{fontWeight: 700, display: {xs: "block", sm: "none"}}}
                            >
                                {t("streamslist.shown", {count: streams.length})}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onAdd}
                            aria-label={t("streamslist.addStream")}
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                display: {xs: "none", sm: "inline-flex"},
                                "&:hover": {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            })}
                        >
                            <AddIcon fontSize="small"/>
                        </IconButton>

                        <Button
                            onClick={onAdd}
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon fontSize="small"/>}
                            sx={{
                                display: {xs: "inline-flex", sm: "none"},
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 800,
                                py: 0.95,
                            }}
                        >
                            {t("streamslist.addStream")}
                        </Button>
                    </Box>

                    {streams.length === 0 ? (
                        <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                {t("streamslist.noStreamsFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("streamslist.noStreamsDescription")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <Box sx={{display: {xs: "none", md: "block"}, width: "100%", overflowX: "auto"}}>
                                <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                    <thead>
                                    <tr style={{background: "#fafafa"}}>
                                        <AppTableHeaderCell>
                                            {t("streamslist.stream")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("streamslist.startingDate")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("streamslist.endingDate")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell align="right">
                                            {t("common.actions")}
                                        </AppTableHeaderCell>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {streams.map((stream) => (
                                        <tr
                                            key={stream.id}
                                            style={{
                                                transition: "background-color 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#fafafa";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "transparent";
                                            }}
                                        >
                                            <td style={{padding: "18px 16px"}}>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "50%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            bgcolor: "grey.300",
                                                            color: "text.primary",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getInitials(stream.name)}
                                                    </Box>

                                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                                        {stream.name || "—"}
                                                    </Typography>
                                                </Box>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {stream.startingDate || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {stream.endingDate || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px", textAlign: "right"}}>
                                                <Box sx={{display: "inline-flex", gap: 1}}>
                                                    <Tooltip title={t("streamslist.manageUsers")}>
                                                        <IconButton
                                                            component={RouterLink}
                                                            to={`/admin/streams/${stream.id}/users`}
                                                            size="small"
                                                            aria-label={t("streamslist.manageUsers")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <PeopleIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={t("streamslist.schedule")}>
                                                        <IconButton
                                                            component={RouterLink}
                                                            to={`/admin/streams/${stream.id}`}
                                                            size="small"
                                                            aria-label={t("streamslist.schedule")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <EventNoteIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={t("common.edit")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEdit(stream)}
                                                            aria-label={t("common.edit")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <EditIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setStreamToDelete(stream.id)}
                                                            aria-label={t("common.delete")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <DeleteIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </Box>

                            {/* Mobile cards */}
                            <Box
                                sx={{
                                    display: {xs: "flex", md: "none"},
                                    flexDirection: "column",
                                    gap: 1.25,
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    sx={(theme) => ({
                                        p: 1.5,
                                        borderRadius: 3,
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                                : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                                        border: `1px solid ${theme.palette.divider}`,
                                    })}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                                        <Box sx={{minWidth: 0}}>
                                            <Typography sx={{fontWeight: 900, lineHeight: 1.2}}>
                                                {t("streamslist.adminStreams")}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                {t("streamslist.adminStreamsDescription")}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            label={t("streamslist.shown", {count: streams.length})}
                                            sx={(theme) => ({
                                                fontWeight: 800,
                                                backgroundColor: theme.palette.action.hover,
                                                flexShrink: 0,
                                            })}
                                        />
                                    </Box>
                                </Box>

                                {streams.map((stream) => (
                                    <Box
                                        key={stream.id}
                                        sx={(theme) => ({
                                            p: 1.5,
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                            boxShadow:
                                                theme.palette.mode === "dark"
                                                    ? "0 14px 32px rgba(0,0,0,0.32)"
                                                    : "0 14px 32px rgba(15,23,42,0.07)",
                                            overflow: "hidden",
                                            position: "relative",
                                            "&:before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                width: 4,
                                                backgroundColor: theme.palette.primary.main,
                                            },
                                        })}
                                    >
                                        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.25}}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    bgcolor: "grey.300",
                                                    color: "text.primary",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {getInitials(stream.name)}
                                            </Box>

                                            <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        lineHeight: 1.35,
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {stream.name || "—"}
                                                </Typography>

                                                <Box
                                                    sx={(theme) => ({
                                                        mt: 1,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        px: 1,
                                                        py: 0.45,
                                                        borderRadius: 999,
                                                        backgroundColor: theme.palette.action.hover,
                                                    })}
                                                >
                                                    <EventNoteIcon
                                                        sx={{
                                                            fontSize: 15,
                                                            color: "text.secondary",
                                                        }}
                                                    />

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{fontWeight: 800}}
                                                    >
                                                        {t("streamslist.streamId")}: {stream.id}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={(theme) => ({
                                                mt: 1.5,
                                                p: 1.25,
                                                borderRadius: 2.5,
                                                backgroundColor: theme.palette.action.hover,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            })}
                                        >
                                            <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                                                <DateRangeOutlinedIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: "text.secondary",
                                                        mt: 0.15,
                                                        flexShrink: 0,
                                                    }}
                                                />

                                                <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{fontWeight: 800, display: "block"}}
                                                    >
                                                        {t("streamslist.startingDate")}
                                                    </Typography>

                                                    <Typography variant="body2" sx={{fontWeight: 700}}>
                                                        {stream.startingDate || "—"}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                                                <DateRangeOutlinedIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: "text.secondary",
                                                        mt: 0.15,
                                                        flexShrink: 0,
                                                    }}
                                                />

                                                <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{fontWeight: 800, display: "block"}}
                                                    >
                                                        {t("streamslist.endingDate")}
                                                    </Typography>

                                                    <Typography variant="body2" sx={{fontWeight: 700}}>
                                                        {stream.endingDate || "—"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: 1,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                component={RouterLink}
                                                to={`/admin/streams/${stream.id}/users`}
                                                startIcon={<PeopleIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("streamslist.users")}
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                component={RouterLink}
                                                to={`/admin/streams/${stream.id}`}
                                                startIcon={<EventNoteIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("streamslist.schedule")}
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => openEdit(stream)}
                                                startIcon={<EditIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("common.edit")}
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                onClick={() => setStreamToDelete(stream.id)}
                                                startIcon={<DeleteIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("common.delete")}
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>

            <DeleteConfirmationModal
                open={streamToDelete !== null}
                onClose={() => (deleteLoading ? null : setStreamToDelete(null))}
                onConfirm={handleDeleteStream}
            />

            <Modal open={editingStream !== null} onClose={closeEditModal}>
                <AppModalBox
                    sx={{
                        p: {xs: 2, sm: 3, md: 4},
                        margin: "auto",
                        width: {xs: "calc(100vw - 24px)", sm: 520},
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                        <Box
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 13,
                                fontWeight: 900,
                                bgcolor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                flexShrink: 0,
                            })}
                        >
                            {editingStream ? getInitials(editingStream.name) : "?"}
                        </Box>

                        <Box sx={{minWidth: 0}}>
                            <Typography variant="h6" sx={{fontWeight: 900, lineHeight: 1.2}} noWrap>
                                {t("streamslist.editStream")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" noWrap>
                                {editingStream?.name ?? t("streamslist.updateStreamDetails")}
                            </Typography>
                        </Box>
                    </Box>

                    {editModalLoading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <form onSubmit={handleEditStream}>
                            <TextField
                                label={t("streamslist.name")}
                                fullWidth
                                required
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("streamslist.startingDate")}
                                type="date"
                                fullWidth
                                required
                                value={editStartingDate}
                                onChange={(e) => setEditStartingDate(e.target.value)}
                                disabled={editSaving}
                                InputLabelProps={{shrink: true}}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("streamslist.endingDateOptional")}
                                type="date"
                                fullWidth
                                value={editEndingDate}
                                onChange={(e) => setEditEndingDate(e.target.value)}
                                disabled={editSaving}
                                InputLabelProps={{shrink: true}}
                                sx={{mb: 2}}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "flex-end",
                                    flexDirection: {xs: "column-reverse", sm: "row"},
                                }}
                            >
                                <Button
                                    onClick={closeEditModal}
                                    disabled={editSaving}
                                    sx={{
                                        textTransform: "none",
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    {t("common.cancel")}
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={editSaving}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 800,
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    {editSaving ? t("streamslist.saving") : t("streamslist.saveChanges")}
                                </Button>
                            </Box>
                        </form>
                    )}
                </AppModalBox>
            </Modal>
        </>
    );
};

export default StreamsList;